import { useCallback, useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  AlertCircle,
  Check,
  ChevronDown,
  Loader2,
  MessageCircle,
  RefreshCw,
  Send,
} from 'lucide-react'
import {
  answerContextQuestion,
  dismissContextQuestion,
  getContextQuestions,
  type ContextQuestion,
} from '../lib/api'
import { SocShell } from '../components/soc/SocShell'
import { EmptyState } from '../components/EmptyState'
import { timeAgo } from '../lib/utils'
import { businessHourFromUtc } from '../lib/businessTime'

/**
 * "Your team is asking" — the Context-Keeper inbox (docs vision §
 * "The Context-Keeper: patterns of life as the moat").
 *
 * The team notices a change in someone's patterns of life ("Paula's started
 * emailing a new external domain a lot — new client, or suspicious?") and ASKS.
 * It is a question, never a verdict. The client answers in free text (folded
 * back into patterns of life so the team gets smarter) or sets it aside.
 *
 * Honesty rules that matter here:
 * - The list endpoint returns ONLY open questions. There is no answered-history
 *   to fetch, so we never fabricate one. The "Recently answered" section holds
 *   only what the client answered in THIS session — real, just-happened actions.
 * - Empty and error states are honest: no invented questions, ever.
 */

// A question the client answered during this session, kept so the card can
// transition in place to an acknowledged state instead of vanishing.
interface AnsweredEntry {
  question: ContextQuestion
  answerText: string
}

// Turn the measured `supporting_facts` into plain, human-readable lines. The
// shape varies by detector `kind`; we render only the keys that are present and
// never invent a number. Unknown shapes fall back to a readable key/value dump
// so a new detector still shows *something* true rather than nothing.
function describeFacts(facts: Record<string, unknown>): string[] {
  const num = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) ? v : null
  const str = (v: unknown): string | null =>
    typeof v === 'string' && v.trim() ? v : null

  const lines: string[] = []
  const consumed = new Set<string>()
  const mark = (...keys: string[]) => keys.forEach((k) => consumed.add(k))

  // New external contact: "5 emails to newdomain.co.nz in the last 7 days;
  // 0 in the 30 days before."
  const recentCount = num(facts.recent_count)
  const windowDays = num(facts.window_days)
  if (recentCount !== null && windowDays !== null) {
    const domain = str(facts.domain)
    const target = domain ? `to ${domain}` : 'to a new external domain'
    const plural = recentCount === 1 ? 'email' : 'emails'
    lines.push(
      `${recentCount} ${plural} ${target} in the last ${windowDays} ` +
        `${windowDays === 1 ? 'day' : 'days'}`,
    )
    mark('recent_count', 'window_days', 'domain')

    const priorCount = num(facts.prior_count)
    const priorDays = num(facts.prior_days)
    if (priorCount !== null && priorDays !== null) {
      lines.push(
        `${priorCount} in the ${priorDays} ` +
          `${priorDays === 1 ? 'day' : 'days'} before`,
      )
      mark('prior_count', 'prior_days')
    }
    const example = str(facts.example_recipient)
    if (example) {
      lines.push(`Most recent: ${example}`)
      mark('example_recipient')
    }
  }

  // New device.
  const deviceFamily = str(facts.device_family)
  if (deviceFamily) {
    const firstSeen = str(facts.first_seen)
    lines.push(
      firstSeen
        ? `First sign-in from a ${deviceFamily}, ${timeAgo(firstSeen)}`
        : `First sign-in from a ${deviceFamily}`,
    )
    mark('device_family', 'first_seen')
  }

  // New working hours.
  const recentHour = num(facts.recent_median_hour_utc)
  const priorHour = num(facts.prior_median_hour_utc)
  if (recentHour !== null && priorHour !== null) {
    lines.push(
      // BRIEF-E5d: the business's own clock, never UTC.
      `Now most active around ${businessHourFromUtc(recentHour)}, ` +
        `was ${businessHourFromUtc(priorHour)}`,
    )
    mark('recent_median_hour_utc', 'prior_median_hour_utc')
    const shift = num(facts.shift_hours)
    if (shift !== null) {
      lines.push(`A shift of about ${Math.abs(shift)} hours`)
      mark('shift_hours')
    }
    const recentSignins = num(facts.recent_signins)
    const priorSignins = num(facts.prior_signins)
    if (recentSignins !== null && priorSignins !== null) {
      lines.push(
        `${recentSignins} recent sign-ins vs ${priorSignins} before`,
      )
      mark('recent_signins', 'prior_signins')
    }
  }

  // Honest fallback for any primitive facts a detector adds that we don't yet
  // have a phrasing for — surfaced as a readable label, never dropped silently.
  for (const [key, value] of Object.entries(facts)) {
    if (consumed.has(key)) continue
    if (num(value) === null && str(value) === null) continue
    lines.push(`${humanizeKey(key)}: ${value}`)
  }

  return lines
}

function humanizeKey(key: string): string {
  const words = key.replace(/_/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

interface QuestionCardProps {
  question: ContextQuestion
  onAnswered: (answerText: string) => void
  onDismissed: () => void
}

function QuestionCard({ question, onAnswered, onDismissed }: QuestionCardProps) {
  const [answerText, setAnswerText] = useState('')
  const [busy, setBusy] = useState<'answer' | 'dismiss' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const facts = describeFacts(question.supporting_facts)

  const submitAnswer = useCallback(async () => {
    const text = answerText.trim()
    if (!text || busy) return
    setBusy('answer')
    setError(null)
    try {
      await answerContextQuestion(question.id, text)
      onAnswered(text)
    } catch {
      setError('That didn’t send. Please try again.')
      setBusy(null)
    }
  }, [answerText, busy, question.id, onAnswered])

  const submitDismiss = useCallback(async () => {
    if (busy) return
    setBusy('dismiss')
    setError(null)
    try {
      await dismissContextQuestion(question.id)
      onDismissed()
    } catch {
      setError('Couldn’t set that aside. Please try again.')
      setBusy(null)
    }
  }, [busy, question.id, onDismissed])

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.25 }}
      className="rounded-2xl border border-gray-200 bg-white p-4 mb-4 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-brass/10 flex items-center justify-center flex-shrink-0">
          <MessageCircle className="w-4 h-4 text-brass" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          {question.subject && (
            <p className="text-xs font-medium text-brass mb-0.5">
              About {question.subject}
            </p>
          )}
          <p className="text-[15px] leading-snug text-navy">
            {question.question_text}
          </p>

          {facts.length > 0 && (
            <ul className="mt-3 space-y-1 rounded-xl bg-cream/70 border border-gray-100 p-3">
              {facts.map((fact, i) => (
                <li
                  key={i}
                  className="text-xs text-gray-600 leading-relaxed"
                >
                  {fact}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-3">
        <label htmlFor={`answer-${question.id}`} className="sr-only">
          Your answer
        </label>
        <textarea
          id={`answer-${question.id}`}
          value={answerText}
          onChange={(e) => setAnswerText(e.target.value)}
          placeholder="Tell your team what’s going on…"
          rows={2}
          disabled={busy !== null}
          className="w-full resize-none rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brass/40 disabled:opacity-60"
        />
        {error && (
          <p className="mt-1 text-xs text-danger" role="alert">
            {error}
          </p>
        )}
        <div className="mt-2 flex items-center justify-between">
          <button
            type="button"
            onClick={submitDismiss}
            disabled={busy !== null}
            className="tap-min text-xs text-gray-400 hover:text-gray-600 disabled:opacity-50 transition-colors"
          >
            Set aside
          </button>
          <button
            type="button"
            onClick={submitAnswer}
            disabled={busy !== null || answerText.trim() === ''}
            className="tap-min inline-flex items-center gap-1.5 rounded-lg bg-brass px-3.5 py-2 text-sm font-medium text-white hover:bg-brass-dark disabled:opacity-40 transition-colors"
          >
            {busy === 'answer' ? (
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
            ) : (
              <Send className="w-4 h-4" aria-hidden="true" />
            )}
            Send
          </button>
        </div>
      </div>
    </motion.div>
  )
}

export function TeamQuestionsPage() {
  const [questions, setQuestions] = useState<ContextQuestion[]>([])
  const [answered, setAnswered] = useState<AnsweredEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showAnswered, setShowAnswered] = useState(false)

  const load = useCallback(async () => {
    setError(null)
    try {
      const data = await getContextQuestions()
      // The endpoint returns only OPEN questions; keep that contract explicit.
      setQuestions(data.filter((q) => q.status === 'open'))
    } catch {
      setError('We couldn’t load your team’s questions right now.')
    }
  }, [])

  useEffect(() => {
    void (async () => {
      setIsLoading(true)
      await load()
      setIsLoading(false)
    })()
  }, [load])

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true)
    await load()
    setIsRefreshing(false)
  }, [load])

  const handleAnswered = useCallback(
    (question: ContextQuestion, answerText: string) => {
      setQuestions((prev) => prev.filter((q) => q.id !== question.id))
      setAnswered((prev) => [{ question, answerText }, ...prev])
    },
    [],
  )

  const handleDismissed = useCallback((questionId: string) => {
    setQuestions((prev) => prev.filter((q) => q.id !== questionId))
  }, [])

  return (
    <SocShell current="questions" mainClassName="box-border w-full max-w-[480px] mx-auto px-4 py-5">
        <div className="flex items-center justify-between mb-1">
          <h1 className="text-lg font-semibold text-navy flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-brass" aria-hidden="true" />
            Your team is asking
          </h1>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing || isLoading}
            className="tap-min p-2 rounded-lg text-gray-400 hover:text-brass hover:bg-brass/10 disabled:opacity-50 transition-colors"
            aria-label="Refresh questions"
          >
            <RefreshCw
              className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-5">
          A few things your team noticed. Your answers help them tell normal from
          not.
        </p>

        {isLoading ? (
          <div
            className="flex items-center justify-center py-12 text-gray-400"
            role="status"
            aria-live="polite"
          >
            <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
            <span className="ml-2 text-sm">Loading…</span>
          </div>
        ) : error ? (
          <div
            className="flex items-start gap-2 rounded-xl border border-danger/30 bg-danger-light p-4 text-sm text-danger"
            role="alert"
          >
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
            <span>{error} Nothing is shown because nothing could be loaded.</span>
          </div>
        ) : questions.length === 0 ? (
          <EmptyState
            icon={MessageCircle}
            title="Nothing right now"
            message="Your team will ask when something new shows up."
          />
        ) : (
          <AnimatePresence initial={false}>
            {questions.map((q) => (
              <QuestionCard
                key={q.id}
                question={q}
                onAnswered={(text) => handleAnswered(q, text)}
                onDismissed={() => handleDismissed(q.id)}
              />
            ))}
          </AnimatePresence>
        )}

        {/* Recently answered — only what happened this session, collapsed. */}
        {answered.length > 0 && (
          <div className="mt-6">
            <button
              type="button"
              onClick={() => setShowAnswered((s) => !s)}
              className="tap-min flex w-full items-center justify-between text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
              aria-expanded={showAnswered}
            >
              <span>Recently answered ({answered.length})</span>
              <ChevronDown
                className={`w-4 h-4 transition-transform ${showAnswered ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </button>
            {showAnswered && (
              <div className="mt-3 space-y-3">
                {answered.map(({ question, answerText }) => (
                  <div
                    key={question.id}
                    className="rounded-xl border border-gray-100 bg-white/60 p-3"
                  >
                    <p className="text-xs text-gray-500 leading-snug">
                      {question.question_text}
                    </p>
                    <div className="mt-2 flex items-start gap-1.5">
                      <Check
                        className="w-3.5 h-3.5 text-success mt-0.5 shrink-0"
                        aria-hidden="true"
                      />
                      <p className="text-xs text-navy">
                        <span className="text-gray-500">You said:</span>{' '}
                        {answerText}
                      </p>
                    </div>
                    <p className="mt-1.5 pl-5 text-[11px] text-success">
                      Got it. Your team will factor that in.
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
    </SocShell>
  )
}
